import { format } from "date-fns";

/**
 * Formats a date for meeting display (e.g., "January 14, 2026").
 * @param date - The date to format
 */
export const formatMeetingDate = (date: Date): string => {
	return format(date, "MMMM d, yyyy");
};

/**
 * Formats a time for meeting display (e.g., "3:30pm").
 * @param date - The date to format
 */
export const formatMeetingTime = (date: Date): string => {
	return format(date, "h:mmaaa");
};

/**
 * Formats a time without AM/PM suffix (e.g., "3:30").
 * @param date - The date to format
 */
export const formatMeetingTimeShort = (date: Date): string => {
	return format(date, "h:mm");
};

/**
 * Formats a date with time (e.g., "January 14, 2026 • 3:30pm").
 * @param date - The date to format
 */
export const formatMeetingDateWithTime = (date: Date): string => {
	return format(date, "MMMM d, yyyy • h:mmaaa");
};

/**
 * Formats a time range for same-day meetings (e.g., "3:30pm–4:30pm").
 * @param start - The start time
 * @param end - The end time
 */
export const formatMeetingTimeRange = (start: Date, end: Date): string => {
	return `${formatMeetingTime(start)}–${formatMeetingTime(end)}`;
};

/**
 * Checks if two dates are on the same calendar day.
 * @param date1 - The first date
 * @param date2 - The second date
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
	return date1.toDateString() === date2.toDateString();
};

/**
 * Formats a complete meeting date/time display.
 * Handles both same-day and multi-day meetings.
 * @param startDateTime - The meeting start time
 * @param endDateTime - The meeting end time
 */
export const formatMeetingDateTimeDisplay = (
	startDateTime: Date,
	endDateTime: Date
): { dateStr: string; timeStr: string; isMultiDay: boolean } => {
	const start = new Date(startDateTime);
	const end = new Date(endDateTime);
	const sameDay = isSameDay(start, end);

	if (sameDay) {
		return {
			dateStr: formatMeetingDate(start),
			timeStr: formatMeetingTimeRange(start, end),
			isMultiDay: false,
		};
	}

	return {
		dateStr: `${formatMeetingDateWithTime(start)}–${formatMeetingDateWithTime(end)}`,
		timeStr: "",
		isMultiDay: true,
	};
};

/**
 * Formats a past meeting's date and time range display.
 * Example: "January 29, 2026 • 9:00 – 9:30am"
 * @param startDateTime - The meeting start time
 * @param endDateTime - The meeting end time
 */
export const formatPastMeetingDateTime = (startDateTime: Date, endDateTime: Date): string => {
	const start = new Date(startDateTime);
	const end = new Date(endDateTime);
	const sameDay = isSameDay(start, end);

	if (sameDay) {
		return `${formatMeetingDate(start)} • ${formatMeetingTimeShort(start)}–${formatMeetingTime(end)}`;
	}

	return `${formatMeetingDateWithTime(start)}–${formatMeetingDateWithTime(end)}`;
};
